import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import {
  CloseCircleFill20Px,
  ProgressButton,
  useToast
} from "@qubit/autoparts";
import { skipToken } from "@reduxjs/toolkit/query";
import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";

import {
  mixpanelTrack,
  mixpanelTrackPickWrapper,
  useMixpanelModalName
} from "~/lib/mixpanel-tracking";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { selectPickingStateArgs } from "~/redux/selectors/autostoreSelectors";
import { selectWorkstationId } from "~/redux/selectors/workstationsSelectors";
import {
  useGetBatchPickingStateQuery,
  useGetTotesForBatchQuery,
  useRepickProductsMutation
} from "~/redux/warehouse/autostorePicking.hooks";
import { MeasuredValueDto, RepickProductsRequest } from "~/types/api";

type RepickProductsModalProps = {
  open: boolean;
  totePosition: number;
  onClose: () => void;
};

type RepickQuantity = {
  variantId: string;
  quantityToRepick: number;
  maxQuantity: number;
  units: string;
  productName: string;
  /** Remaining weights still shown as chips */
  actualWeights: MeasuredValueDto[];
  /** Weights selected for removal / repick (sent as weightsToRemove) */
  weightsToRemove: MeasuredValueDto[];
};

const isWeightedItem = (item: RepickQuantity) =>
  item.actualWeights.length > 0 || item.weightsToRemove.length > 0;

function RepickProductsModal({
  open,
  totePosition,
  onClose
}: RepickProductsModalProps) {
  const { successToast, warningToast, errorToast } = useToast();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const trackedModalName = useMixpanelModalName("Re-pick Products");

  const trackAndClose = (label: string) => {
    mixpanelTrack({
      trackedPageName: trackedModalName,
      type: "Button Click",
      label
    });
    onClose();
  };

  const workstationId = useAppSelector(selectWorkstationId);
  const pickingStateArgs = useAppSelector(selectPickingStateArgs);
  const { data: pickingState } = useGetBatchPickingStateQuery(pickingStateArgs);
  const batchId = pickingState?.batchId;
  const [repickQuantities, setRepickQuantities] = useState<RepickQuantity[]>(
    []
  );

  const { toteId, toteContents, isLoading } = useGetTotesForBatchQuery(
    batchId && totePosition
      ? {
          batchId
        }
      : skipToken,
    {
      refetchOnMountOrArgChange: 5,
      selectFromResult: ({ data, isLoading }) => {
        const tote = data
          ?.find((pos) => pos.position === totePosition)
          ?.totes?.find((tote) => !tote.labelPlacedTimestamp);

        return {
          toteId: tote?.toteId,
          toteContents: tote?.contents,
          isLoading
        };
      }
    }
  );

  // initialize data
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (toteContents && !repickQuantities.length) {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-chain-state-updates
      setRepickQuantities(
        toteContents
          ?.filter((ele) => ele.quantity.value > 0)
          .map((contentItem) => {
            const currentQuantity = repickQuantities.find(
              (ele) => ele.variantId === contentItem.variantId
            )?.quantityToRepick;

            return {
              variantId: contentItem.variantId,
              quantityToRepick: currentQuantity ?? 0,
              maxQuantity: contentItem.quantity.value,
              units: contentItem.quantity.units,
              productName: contentItem.productName,
              actualWeights: contentItem.actualWeights,
              weightsToRemove: []
            } as RepickQuantity;
          }) || []
      );
    }
  }, [toteContents, repickQuantities]);

  const totalRepickQuantity = repickQuantities.reduce(
    (acc, ele) => acc + ele.quantityToRepick,
    0
  );

  const { t } = useTranslation();

  const [repickProducts] = useRepickProductsMutation();

  const incrementQuantity = (variantId: string) => {
    setRepickQuantities(
      repickQuantities.map((ele) =>
        ele.variantId === variantId
          ? {
              ...ele,
              quantityToRepick: Math.min(
                ele.maxQuantity,
                ele.quantityToRepick + 1
              )
            }
          : ele
      )
    );
  };

  const decrementQuantity = (variantId: string) => {
    setRepickQuantities(
      repickQuantities.map((ele) =>
        ele.variantId === variantId
          ? {
              ...ele,
              quantityToRepick: Math.max(0, ele.quantityToRepick - 1)
            }
          : ele
      )
    );
  };

  const removeWeight = (variantId: string, weightIndexToRemove: number) => {
    setRepickQuantities(
      repickQuantities.map((ele) => {
        if (ele.variantId !== variantId) return ele;
        const weight = ele.actualWeights[weightIndexToRemove];
        if (!weight) return ele;
        const weightsToRemove = [...ele.weightsToRemove, weight];
        return {
          ...ele,
          actualWeights: ele.actualWeights.filter(
            (_, i) => i !== weightIndexToRemove
          ),
          weightsToRemove,
          quantityToRepick: weightsToRemove.length
        };
      })
    );
  };

  const clearAllWeights = (variantId: string) => {
    setRepickQuantities(
      repickQuantities.map((ele) => {
        if (ele.variantId !== variantId) return ele;
        const weightsToRemove = [...ele.weightsToRemove, ...ele.actualWeights];
        return {
          ...ele,
          actualWeights: [],
          weightsToRemove,
          quantityToRepick: weightsToRemove.length
        };
      })
    );
  };

  const repickAll = () => {
    setRepickQuantities(
      repickQuantities.map((ele) => {
        if (isWeightedItem(ele)) {
          const weightsToRemove = [
            ...ele.weightsToRemove,
            ...ele.actualWeights
          ];
          return {
            ...ele,
            actualWeights: [],
            weightsToRemove,
            quantityToRepick: weightsToRemove.length
          };
        }
        return {
          ...ele,
          quantityToRepick: ele.maxQuantity
        };
      })
    );
  };

  const repickNone = () => {
    setRepickQuantities(
      repickQuantities.map((ele) => {
        return {
          ...ele,
          quantityToRepick: 0,
          actualWeights: [...ele.actualWeights, ...ele.weightsToRemove],
          weightsToRemove: []
        };
      })
    );
  };

  const handleRepickConfirm = async () => {
    if (repickQuantities?.length) {
      if (batchId && toteId && workstationId) {
        mixpanelTrackPickWrapper(
          trackedModalName,
          "Button Click",
          "Confirm",
          totePosition
        );
        try {
          const request: RepickProductsRequest = {
            toteId,
            batchId,
            workstationId,
            repick: repickQuantities
              .filter((contentLocal) => contentLocal.quantityToRepick > 0)
              .map((repick) => ({
                variantId: repick.variantId,
                quantity: {
                  value: repick.quantityToRepick,
                  units: repick.units
                },
                ...(repick.weightsToRemove.length > 0
                  ? { weightsToRemove: repick.weightsToRemove }
                  : {})
              })),
            reason: "Damaged"
          };

          const message = await repickProducts(request).unwrap();

          if (message.includes("Some picks could not be rescheduled"))
            warningToast(message);
          else successToast(message);

          onClose();
        } catch (err) {
          errorToast(getMessageFromRtkError(err), {
            trackingLocation: trackedModalName
          });
        }
      }
    }
  };

  if (isLoading) {
    return (
      <Dialog
        onClose={() => trackAndClose("Close")}
        open={open}
        fullScreen={fullScreen}
        maxWidth={"lg"}
      >
        <DialogTitle variant="h5" fontWeight="bold" aria-label="dialog-title">
          {t("repick products")}
        </DialogTitle>
        <DialogContent sx={{ mX: "auto", my: 2 }}>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  } else if (!repickQuantities.length) {
    return (
      <Dialog
        onClose={() => trackAndClose("Close")}
        open={open}
        maxWidth={"lg"}
      >
        <DialogTitle variant="h5" fontWeight="bold" aria-label="dialog-title">
          {t("repick products")}
        </DialogTitle>
        <DialogContent>
          <Alert
            sx={{ marginTop: "20px" }}
            aria-label={`maximum-quantity-repicked`}
            severity={"error"}
          >
            <Typography variant="h6">
              {t("the maximum amount repicked")}
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", mb: 2 }}>
          <ProgressButton
            color="primary"
            onClick={() => trackAndClose("Ok")}
            fullWidth
            buttonSize="medium"
            emphasis="high"
            responsive
            aria-label="close-repick-button"
          >
            {t("ok")}
          </ProgressButton>
        </DialogActions>
      </Dialog>
    );
  } else {
    return (
      <Dialog
        onClose={() => trackAndClose("Close")}
        open={open}
        fullScreen={fullScreen}
        maxWidth={"md"}
        fullWidth={true}
      >
        <DialogTitle variant="h5" fontWeight="bold" aria-label="dialog-title">
          {t("repick products")}
        </DialogTitle>
        <DialogContent>
          <Grid container direction="column">
            <Fragment key={toteId}>
              {repickQuantities.map((contentItem, i) => (
                <Grid
                  key={`repick-${i}`}
                  item
                  container
                  justifyContent="center"
                  alignItems="center"
                  sx={{
                    paddingTop: "10px",
                    paddingLeft: "10px"
                  }}
                >
                  <Grid item xs={9}>
                    <Typography
                      variant="h5"
                      fontWeight="bold"
                      aria-label={contentItem.productName}
                    >
                      {contentItem.productName}
                    </Typography>
                    {contentItem.actualWeights.length > 0 && (
                      <Stack
                        direction="row"
                        flexWrap="wrap"
                        gap={1}
                        alignItems="center"
                        mt={1}
                      >
                        {contentItem.actualWeights.map(
                          (weight, weightIndex) => (
                            <Chip
                              key={`${contentItem.variantId}-${weight.value}-${weightIndex}`}
                              label={`${weight.value} ${weight.units}`}
                              variant="outlined"
                              deleteIcon={
                                <CloseCircleFill20Px
                                  width={20}
                                  height={20}
                                  style={{ marginLeft: 0 }}
                                />
                              }
                              onDelete={() => {
                                removeWeight(
                                  contentItem.variantId,
                                  weightIndex
                                );
                              }}
                              style={{
                                height: "42px"
                              }}
                              sx={{
                                borderColor: theme.palette.warning.dark,
                                backgroundColor: theme.palette.warning.light,
                                fontSize: "1.25rem",
                                textTransform: "uppercase"
                              }}
                            />
                          )
                        )}
                        <ProgressButton
                          color="primary"
                          emphasis="low"
                          buttonSize="small"
                          onClick={() => {
                            clearAllWeights(contentItem.variantId);
                          }}
                          aria-label={`clear-all-weights-${contentItem.productName}`}
                        >
                          {t("clear all")}
                        </ProgressButton>
                      </Stack>
                    )}
                  </Grid>
                  {isWeightedItem(contentItem) ? (
                    <Grid item xs={1} />
                  ) : contentItem.quantityToRepick > 0 ? (
                    <Grid
                      item
                      xs={1}
                      sx={{
                        textAlign: "right"
                      }}
                    >
                      <RemoveIcon
                        fontSize="large"
                        onClick={() => {
                          mixpanelTrack({
                            trackedPageName: trackedModalName,
                            type: "Button Click",
                            label: "Decrease Quantity",
                            eventProperties: {
                              Quantity: contentItem.quantityToRepick - 1,
                              "Product Name": contentItem.productName
                            }
                          });
                          decrementQuantity(contentItem.variantId);
                        }}
                        sx={{
                          "&:hover": {
                            cursor: "pointer"
                          }
                        }}
                        aria-label={`repick-${contentItem.productName}-minus`}
                      />
                    </Grid>
                  ) : (
                    <Grid item xs={1} />
                  )}
                  <Grid
                    item
                    xs={1}
                    alignItems="center"
                    justifyContent="center"
                    sx={{ textAlign: "center" }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      sx={{
                        textAlign: "center"
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{
                          color:
                            contentItem.quantityToRepick === 0
                              ? "gray.main"
                              : undefined,
                          letterSpacing: 4
                        }}
                        aria-label={`${contentItem.productName}-quantity-to-repick`}
                      >
                        {contentItem.quantityToRepick}
                      </Typography>
                      <Typography
                        variant="h5"
                        sx={{ letterSpacing: 4 }}
                        aria-label={`${contentItem.productName}-quantity-total`}
                      >
                        {`/${contentItem.maxQuantity}`}
                      </Typography>
                    </Box>
                  </Grid>
                  {isWeightedItem(contentItem) ? (
                    <Grid item xs={1} />
                  ) : contentItem.quantityToRepick < contentItem.maxQuantity ? (
                    <Grid item xs={1}>
                      <AddIcon
                        fontSize="large"
                        onClick={() => {
                          mixpanelTrack({
                            trackedPageName: trackedModalName,
                            type: "Button Click",
                            label: "Increase Quantity",
                            eventProperties: {
                              Quantity: contentItem.quantityToRepick + 1,
                              "Product Name": contentItem.productName
                            }
                          });
                          incrementQuantity(contentItem.variantId);
                        }}
                        sx={{
                          "&:hover": {
                            cursor: "pointer"
                          }
                        }}
                        aria-label={`repick-${contentItem.productName}-plus`}
                      />
                    </Grid>
                  ) : (
                    <Grid item xs={1} />
                  )}
                </Grid>
              ))}
            </Fragment>
            <Grid item sx={{ paddingTop: "20px", paddingBottom: "20px" }}>
              <Divider />
            </Grid>
            <Grid
              item
              container
              justifyContent="center"
              alignItems="center"
              sx={{
                paddingLeft: "10px"
              }}
            >
              <Grid item xs={9}>
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  aria-label="repick-all-text"
                >
                  {t("repick all")}
                </Typography>
              </Grid>
              <Grid
                item
                xs={1}
                sx={{
                  textAlign: "right"
                }}
              >
                <RemoveIcon
                  fontSize="large"
                  sx={{
                    "&:hover": {
                      cursor: "pointer"
                    }
                  }}
                  onClick={() => {
                    mixpanelTrack({
                      trackedPageName: trackedModalName,
                      type: "Button Click",
                      label: "Repick None"
                    });
                    repickNone();
                  }}
                  aria-label="repick-all-products-minus"
                />
              </Grid>

              <Grid item xs={1} sx={{ textAlign: "center" }}>
                <Typography variant="h5" aria-label="repick-all-products-value">
                  {totalRepickQuantity}
                </Typography>
              </Grid>
              <Grid item xs={1}>
                <AddIcon
                  fontSize="large"
                  sx={{
                    textAlign: "right",
                    "&:hover": {
                      cursor: "pointer"
                    }
                  }}
                  onClick={() => {
                    mixpanelTrack({
                      trackedPageName: trackedModalName,
                      type: "Button Click",
                      label: "Repick All"
                    });
                    repickAll();
                  }}
                  aria-label="repick-all-products-plus"
                />
              </Grid>
            </Grid>
            <Grid
              item
              container
              spacing={2}
              sx={{
                paddingTop: "20px",
                paddingBottom: "20px",
                marginTop: "20px"
              }}
            >
              <Grid item xs={6} sx={{ display: { md: "none" } }}>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => trackAndClose("Cancel")}
                  fullWidth
                >
                  <Typography variant="h5">{t("cancel")}</Typography>
                </Button>
              </Grid>
              <Grid item md={12} xs={6}>
                <ProgressButton
                  onClick={handleRepickConfirm}
                  fullWidth
                  responsive
                  disabled={totalRepickQuantity <= 0}
                  aria-label="confirm-button"
                >
                  <Typography variant="h5">{t("confirm")}</Typography>
                </ProgressButton>
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    );
  }
}
export default RepickProductsModal;
